import { Box, type BoxProps } from '@mui/material';
import { useFeatureValue } from '@growthbook/growthbook-react';
import type { ReactNode } from 'react';

import type { Jersey } from '../../@types';
import { FEATURE_FLAG } from '../../constants/featureFlags';
import { useMarketplaceConfig } from '../../queries/marketplace';
import JerseyDisplay from '../molecules/JerseyDisplay';
import { CompetitionFlag } from './CompetitionFlag';
import NeutralJerseyPlaceholder from './NeutralJerseyPlaceholder';
import ParticipantLogo from './ParticipantLogo';

type ParticipantAssetProps = {
  competitionName?: string | null;
  fallback?: ReactNode;
  jersey?: Pick<Jersey, 'patternId' | 'firstColor' | 'secondColor' | 'thirdColor'> | null;
  participantId?: string | null;
  participantName?: string | null;
  size?: number | string;
  sportId?: string | null;
  sx?: BoxProps['sx'];
};

const renderJerseyMode = (jersey: ParticipantAssetProps['jersey'], size: number | string, sx?: BoxProps['sx']) => {
  // Jersey mode: jersey → neutral placeholder (no flag fallback)
  const jerseyDisplay = jersey ? (
    <JerseyDisplay {...jersey} width='100%' height='100%' />
  ) : (
    <NeutralJerseyPlaceholder />
  );

  return (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        display: 'block',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {jerseyDisplay}
    </Box>
  );
};

const renderLogoMode = (
  competitionName: string | null | undefined,
  fallback: ReactNode | undefined,
  jersey: ParticipantAssetProps['jersey'],
  size: number | string,
  headSportIcons: string[],
  sportId?: string | null,
  sx?: BoxProps['sx'],
  participantId?: string | null,
  participantName?: string | null
) => {
  // Logo mode fallback chain: flag → jersey (ParticipantLogo handles flag on load failure)
  const flagFallback = competitionName ? (
    <CompetitionFlag name={competitionName} width={size} height={size} sx={sx} />
  ) : null;

  const jerseyFallback = jersey ? <JerseyDisplay {...jersey} width={size} height={size} /> : null;
  const isHeadSportIcon = headSportIcons.includes(sportId ?? '');

  return (
    <ParticipantLogo
      competitionName={competitionName}
      fallback={fallback ?? flagFallback ?? jerseyFallback}
      participantId={participantId}
      participantName={participantName}
      size={size}
      sx={isHeadSportIcon ? { ...sx, borderRadius: '50%' } : sx}
    />
  );
};

const ParticipantAsset = ({
  competitionName,
  fallback,
  jersey,
  participantId,
  participantName,
  size = 20,
  sportId,
  sx,
}: ParticipantAssetProps) => {
  const { data: marketplaceConfig } = useMarketplaceConfig();
  const headSportIcons = useFeatureValue<string[] | null>(FEATURE_FLAG.HEAD_SPORT_ICONS, null) ?? [];
  const teamAssets = marketplaceConfig?.sportsbookConfig?.assetVariants?.teamAssets ?? 'none';

  // Asset variant modes:
  // - 'none': displays nothing
  // - 'logo': displays logo, falls back to competition flag
  // - 'jersey': displays jersey, falls back to neutral placeholder
  if (teamAssets === 'none') {
    return null;
  }

  if (teamAssets === 'jersey') {
    return renderJerseyMode(jersey, size, sx);
  }

  return renderLogoMode(
    competitionName,
    fallback,
    jersey,
    size,
    headSportIcons,
    sportId,
    sx,
    participantId,
    participantName
  );
};

export default ParticipantAsset;
